// frontend/src/components/Sidebar.jsx
// SignalIQ enterprise sidebar — invite-only beta RBAC
// Replace: frontend/src/components/Sidebar.jsx
// Fixes:
// 1) Testers receive full product-module access during the controlled beta.
// 2) Testers are blocked from admin/governance/billing/workspace controls.
// 3) PRO lock badges are suppressed for testers and replaced with clean beta access behavior.
// 4) Desktop expanded/collapsed sidebar, mobile drawer, scroll preservation, and logo handling are retained.
// 5) Mobile responsiveness improved with safe-area padding, touch scrolling, stronger overlay behavior, and better small-screen sizing.

import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Upload,
  Activity,
  AlertTriangle,
  TrendingUp,
  Scale,
  LineChart,
  Target,
  Zap,
  Globe,
  Newspaper,
  ShieldAlert,
  Leaf,
  FileText,
  Users,
  CreditCard,
  Settings,
  ChevronDown,
  ChevronRight,
  Briefcase,
  PieChart,
  Calculator,
  Telescope,
  BarChart3,
  HeartPulse,
  Layers,
  Bot,
  FlaskConical,
  ShieldCheck,
  Cpu,
  Menu,
  X,
  BriefcaseBusiness,
  Lock,
  Sparkles,
  Star,
  Gauge,
  Landmark,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";

import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";

export const APP_ROUTES = {
  dashboard: "/dashboard",
  onboarding: "/onboarding",
  portfolio: "/portfolio",
  companies: "/companies",
  ingestion: "/ingestion",
  ingestionJobs: "/ingestion/jobs",
  bsp: "/business-strength-profile",
  bspSurvival: "/business-survival-probability",
  fhi: "/fhi",
  benchmarks: "/benchmarks",
  covenants: "/covenants",
  earlyWarnings: "/early-warnings",
  variance: "/variance",
  unitEconomics: "/unit-economics",
  decomposition: "/decomposition",
  scenarios: "/scenarios",
  sensitivity: "/sensitivity",
  goalSeek: "/goal-seek",
  forecasting: "/forecasting",
  investmentAppraisal: "/investment-appraisal",
  financialStatementVisuals: "/financial-statement-visuals",
  costBenefit: "/cost-benefit",
  macro: "/macro",
  dailyBriefing: "/daily-briefing",
  riskMonitor: "/risk-monitor",
  esg: "/esg",
  aiAnalyst: "/ai-analyst",
  reports: "/reports",
  team: "/team",
  billing: "/billing",
  settings: "/settings",
  admin: "/admin",
  modelLab: "/model-lab",
  modelGovernance: "/model-governance",
  aiWorkforce: "/ai-workforce",
};

const ADMIN_ROLES = ["admin", "owner"];
const GOVERNANCE_ROLES = ["admin", "owner"];
const BILLING_ROLES = ["admin", "owner", "billing_admin"];
const TESTER_ROLES = ["tester"];
const BETA_ACCESS_ROLES = ["tester"];

// Product visibility tiers.
// Free/public users should never see internal platform tools such as AI Workforce,
// Model Lab, Model Governance, Admin Control, Team, or Billing unless explicitly allowed.
const FREE_TIER_ALIASES = ["", "free", "starter", "viewer", "public", "trial"];
const PRODUCT_TIER_ALIASES = ["pro", "professional", "institutional", "enterprise", "business", "beta"];
const INSTITUTIONAL_TIER_ALIASES = ["institutional", "enterprise", "business", "beta"];

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 76;
const SIDEBAR_SCROLL_STORAGE_KEY = "siq_sidebar_scroll_top";
const SIDEBAR_FAVORITES_STORAGE_KEY = "siq_sidebar_favorites_v1";

function titleCase(v) {
  return String(v || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeRole(role) {
  const raw = String(role || "").trim().toLowerCase();
  const aliases = {
    superuser: "owner",
    super_admin: "owner",
    administrator: "admin",
    team_admin: "admin",
    manager: "admin",
    member: "analyst",
    user: "analyst",
    billing: "billing_admin",
    billingadmin: "billing_admin",
    beta: "tester",
    beta_tester: "tester",
    tester: "tester",
  };
  return aliases[raw] || raw || "viewer";
}

function getUserRole(user) {
  return normalizeRole(
    user?.role ||
      user?.access_role ||
      user?.user_role ||
      user?.account_role ||
      user?.membership_role
  );
}

function normalizeTier(tier) {
  const raw = String(tier || "").trim().toLowerCase();
  const aliases = {
    institutional_plan: "institutional",
    enterprise_plan: "enterprise",
    enterprise_ready: "enterprise",
    premium: "institutional",
    paid: "pro",
    tester: "beta",
    beta_tester: "beta",
  };
  return aliases[raw] || raw || "free";
}

function getUserTier(user) {
  return normalizeTier(
    user?.tier ||
      user?.subscription_tier ||
      user?.plan ||
      user?.plan_tier ||
      user?.subscription?.tier ||
      user?.organization?.tier ||
      user?.organisation?.tier
  );
}

function isInstitutionalTier(tier) {
  return INSTITUTIONAL_TIER_ALIASES.includes(normalizeTier(tier));
}

function isProductTier(tier) {
  return PRODUCT_TIER_ALIASES.includes(normalizeTier(tier));
}

function isFreeTier(tier) {
  return FREE_TIER_ALIASES.includes(normalizeTier(tier));
}

function hasAnyRole(role, allowedRoles = []) {
  return allowedRoles.includes(normalizeRole(role));
}

function isBetaAccessRole(role) {
  return BETA_ACCESS_ROLES.includes(normalizeRole(role));
}

function isTesterRole(role) {
  return TESTER_ROLES.includes(normalizeRole(role));
}

function isAdminOnlyItem(item) {
  return Boolean(
    item?.adminOnly ||
      item?.governanceOnly ||
      item?.modelLabOnly ||
      item?.roles?.some((role) => ADMIN_ROLES.includes(normalizeRole(role)))
  );
}

function canBypassModuleLock(role, item) {
  if (!item?.feature) return false;
  if (isAdminOnlyItem(item)) return false;
  return isBetaAccessRole(role);
}

function shouldShowOnboarding(user) {
  const status = String(user?.onboarding_status || "").toLowerCase();
  return !status || status === "not_started" || status === "in_progress";
}

function SidebarStyles() {
  return (
    <style>{`
      :root {
        --siq-sidebar-expanded: ${EXPANDED_WIDTH}px;
        --siq-sidebar-collapsed: ${COLLAPSED_WIDTH}px;
      }

      .siq-sidebar-desktop,
      .siq-sidebar-mobile,
      .siq-sidebar-desktop *,
      .siq-sidebar-mobile * {
        box-sizing: border-box;
      }

      .siq-sidebar-desktop {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100vh;
        min-height: 100vh;
        flex-shrink: 0;
        overflow: visible;
        background: linear-gradient(180deg, #071120 0%, #0b1728 100%);
        border-right: 1px solid rgba(148, 163, 184, 0.14);
        transition:
          width 180ms ease,
          min-width 180ms ease,
          max-width 180ms ease,
          flex-basis 180ms ease;
        z-index: 30;
      }

      .siq-sidebar-mobile {
        display: none;
      }

      .siq-sidebar-overlay {
        display: none;
      }

      .siq-sidebar-shell {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        width: 100%;
        overflow: hidden;
      }

      .siq-sidebar-beta-strip,
      .siq-sidebar-institutional-strip {
        margin: 10px 12px 0;
        padding: 8px 10px;
        border-radius: 11px;
        border: 1px solid rgba(16, 185, 129, 0.18);
        background: rgba(16, 185, 129, 0.08);
        color: rgba(167, 243, 208, 0.92);
        display: flex;
        align-items: center;
        gap: 7px;
        font-size: 10.5px;
        font-weight: 750;
        letter-spacing: 0.02em;
      }

      .siq-sidebar-institutional-strip {
        border: 1px solid rgba(96, 165, 250, 0.20);
        background: rgba(37, 99, 235, 0.10);
        color: rgba(191, 219, 254, 0.94);
      }

      .siq-sidebar-top {
        height: 88px;
        min-height: 88px;
        max-height: 88px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 10px 12px 10px 16px;
        border-bottom: 1px solid rgba(148, 163, 184, 0.10);
        overflow: visible;
        background: rgba(8, 15, 28, 0.72);
      }

      .siq-sidebar-logo {
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        height: 68px;
        max-width: 100%;
        text-decoration: none;
        overflow: hidden;
      }

      .siq-sidebar-logo-img {
        display: block;
        width: 224px;
        max-width: 224px;
        height: 64px;
        object-fit: contain;
        object-position: left center;
        background: transparent;
        border: 0;
        outline: none;
        filter: brightness(1.05) contrast(1.08) drop-shadow(0 10px 24px rgba(37, 99, 235, 0.20));
      }

      .siq-sidebar-logo-img--compact {
        width: 42px;
        max-width: 42px;
        height: 42px;
        object-fit: contain;
        object-position: center;
        filter: brightness(1.05) contrast(1.08) drop-shadow(0 10px 24px rgba(37, 99, 235, 0.18));
      }

      .siq-sidebar-logo-fallback {
        display: inline-flex;
        align-items: center;
        height: 54px;
        font-family: var(--font-ui, Inter, ui-sans-serif, system-ui);
        font-size: 27px;
        font-weight: 850;
        letter-spacing: -0.06em;
        color: #fff;
        line-height: 1;
      }

      .siq-sidebar-logo-fallback span {
        color: #38bdf8;
      }

      .siq-sidebar-collapse,
      .siq-mobile-menu {
        width: 34px;
        height: 34px;
        min-width: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        border: 1px solid rgba(148, 163, 184, 0.18);
        background: rgba(15, 23, 42, 0.72);
        color: rgba(226, 232, 240, 0.88);
        cursor: pointer;
        transition:
          background 160ms ease,
          border-color 160ms ease,
          color 160ms ease,
          transform 160ms ease;
      }

      .siq-sidebar-collapse:hover,
      .siq-mobile-menu:hover {
        background: rgba(37, 99, 235, 0.18);
        border-color: rgba(96, 165, 250, 0.34);
        color: #fff;
      }

      .siq-sidebar-desktop.is-collapsed {
        overflow: visible;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-shell {
        overflow: hidden;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-top {
        justify-content: center;
        padding: 12px 8px;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-logo {
        justify-content: center;
        width: 100%;
        height: 50px;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-collapse {
        position: absolute;
        right: -17px;
        top: 112px;
        z-index: 80;
        width: 34px;
        height: 34px;
        background: rgba(15, 23, 42, 0.96);
        border-color: rgba(96, 165, 250, 0.30);
        box-shadow: 0 18px 42px rgba(2, 6, 23, 0.48);
      }

      .siq-sidebar-onboarding {
        margin: 12px 12px 10px;
        padding: 11px 12px;
        display: block;
        text-decoration: none;
        border-radius: 12px;
        background: linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(14, 165, 233, 0.10));
        border: 1px solid rgba(96, 165, 250, 0.20);
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
      }

      .siq-sidebar-onboarding-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255, 255, 255, 0.92);
        font-size: 12px;
        font-weight: 750;
      }

      .siq-sidebar-onboarding-copy {
        margin-top: 5px;
        color: rgba(203, 213, 225, 0.72);
        font-size: 10.5px;
        line-height: 1.45;
      }

      .siq-sidebar-nav {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 10px 8px 12px;
        scrollbar-width: thin;
        scrollbar-color: rgba(148, 163, 184, 0.18) transparent;
        scroll-behavior: auto;
      }

      .siq-sidebar-nav::-webkit-scrollbar {
        width: 6px;
      }

      .siq-sidebar-nav::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.18);
        border-radius: 999px;
      }

      .siq-sidebar-group-label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 10px 10px 6px;
        color: rgba(148, 163, 184, 0.74);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        cursor: pointer;
        user-select: none;
      }

      .siq-sidebar-group-label:hover {
        color: rgba(226, 232, 240, 0.88);
      }

      .siq-sidebar-item {
        display: flex;
        align-items: center;
        gap: 11px;
        min-height: 41px;
        width: 100%;
        margin: 2px 0;
        padding: 10px 12px;
        border-radius: 12px;
        color: rgba(203, 213, 225, 0.74);
        text-decoration: none;
        font-size: 14px;
        font-weight: 650;
        line-height: 1;
        border: 1px solid transparent;
        transition:
          background 160ms ease,
          border-color 160ms ease,
          color 160ms ease,
          opacity 160ms ease;
      }

      .siq-sidebar-item:hover {
        background: rgba(37, 99, 235, 0.10);
        color: rgba(255, 255, 255, 0.92);
        border-color: rgba(96, 165, 250, 0.14);
      }

      .siq-sidebar-item.active {
        background: linear-gradient(90deg, rgba(37, 99, 235, 0.26), rgba(37, 99, 235, 0.10));
        color: #fff;
        border-color: rgba(96, 165, 250, 0.20);
      }

      .siq-sidebar-item-icon {
        flex: 0 0 auto;
        opacity: 0.86;
      }

      .siq-sidebar-item-label {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .siq-sidebar-badge {
        font-family: var(--font-mono, "DM Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace);
        font-size: 8px;
        font-weight: 700;
        letter-spacing: 0.06em;
        border-radius: 4px;
        padding: 2px 5px;
        flex-shrink: 0;
        text-transform: uppercase;
      }

      .siq-sidebar-foot {
        flex-shrink: 0;
        padding: 12px 14px 14px;
        border-top: 1px solid rgba(148, 163, 184, 0.10);
        color: rgba(255, 255, 255, 0.42);
        font-size: 10px;
      }

      .siq-sidebar-foot-row {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .siq-sidebar-legal {
        margin-top: 8px;
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }

      .siq-sidebar-legal a {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.22);
        text-decoration: none;
      }

      .siq-sidebar-legal a:hover {
        color: rgba(255, 255, 255, 0.55);
      }

      /* Collapsed icon rail */
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-nav {
        padding: 22px 8px 12px;
        scrollbar-width: none;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-nav::-webkit-scrollbar {
        display: none;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-item {
        justify-content: center;
        width: 46px;
        height: 46px;
        min-height: 46px;
        padding: 0;
        margin: 5px auto;
        border-radius: 14px;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-item-icon {
        margin: 0;
      }

      .siq-sidebar-desktop.is-collapsed .siq-sidebar-item-label,
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-badge,
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-group-label,
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-foot,
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-onboarding,
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-beta-strip,
      .siq-sidebar-desktop.is-collapsed .siq-sidebar-institutional-strip {
        display: none !important;
      }

      .siq-mobile-menu {
        display: none;
        position: fixed;
        top: 14px;
        left: 14px;
        z-index: 70;
        box-shadow: 0 14px 36px rgba(2, 6, 23, 0.30);
      }

      @media (max-width: 900px) {
        .siq-sidebar-desktop {
          display: none !important;
        }

        .siq-mobile-menu {
          display: inline-flex;
          top: calc(12px + env(safe-area-inset-top, 0px));
          left: calc(12px + env(safe-area-inset-left, 0px));
        }

        .siq-sidebar-overlay {
          display: block;
          position: fixed;
          inset: 0;
          z-index: 55;
          background: rgba(2, 6, 23, 0.62);
          backdrop-filter: blur(4px);
        }

        .siq-sidebar-mobile {
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          width: min(88vw, 328px);
          min-width: 0;
          max-width: min(88vw, 328px);
          transform: translateX(-105%);
          transition: transform 190ms ease;
          z-index: 65;
          background: linear-gradient(180deg, #071120 0%, #0b1728 100%);
          border-right: 1px solid rgba(148, 163, 184, 0.14);
          box-shadow: 24px 0 70px rgba(2, 6, 23, 0.44);
          overflow: hidden;
          overscroll-behavior: contain;
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }

        .siq-sidebar-mobile.is-open {
          transform: translateX(0);
        }

        .siq-sidebar-mobile .siq-sidebar-nav {
          -webkit-overflow-scrolling: touch;
          padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
        }

        .siq-sidebar-mobile .siq-sidebar-top {
          padding-left: 54px;
        }
      }

      @media (max-width: 640px) {
        .siq-sidebar-mobile {
          width: 90vw;
          max-width: 90vw;
        }

        .siq-sidebar-logo-img {
          width: 190px;
          max-width: 190px;
        }

        .siq-sidebar-item {
          min-height: 44px;
          font-size: 13.5px;
        }
      }

      @media (max-width: 420px) {
        .siq-sidebar-mobile {
          width: 92vw;
          max-width: 92vw;
        }

        .siq-sidebar-mobile .siq-sidebar-top {
          padding-left: 50px;
        }
      }

      /* ------------------------------------------------------------------ */
      /* Theme-aware sidebar variables                                       */
      /* ------------------------------------------------------------------ */
      :root,
      html[data-theme="dark"],
      body[data-theme="dark"] {
        --siq-sidebar-bg: linear-gradient(180deg, #071120 0%, #0b1728 100%);
        --siq-sidebar-top-bg: rgba(8, 15, 28, 0.72);
        --siq-sidebar-border: rgba(148, 163, 184, 0.14);
        --siq-sidebar-label: rgba(148, 163, 184, 0.74);
        --siq-sidebar-label-hover: rgba(226, 232, 240, 0.88);
        --siq-sidebar-item: rgba(203, 213, 225, 0.78);
        --siq-sidebar-item-hover: rgba(255, 255, 255, 0.94);
        --siq-sidebar-item-hover-bg: rgba(37, 99, 235, 0.10);
        --siq-sidebar-item-active-bg: linear-gradient(90deg, rgba(37, 99, 235, 0.26), rgba(37, 99, 235, 0.10));
        --siq-sidebar-item-active: #ffffff;
        --siq-sidebar-card-bg: linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(14, 165, 233, 0.10));
        --siq-sidebar-card-text: rgba(203, 213, 225, 0.76);
        --siq-sidebar-card-title: rgba(255, 255, 255, 0.94);
        --siq-sidebar-foot: rgba(255, 255, 255, 0.42);
        --siq-sidebar-legal: rgba(255, 255, 255, 0.28);
        --siq-sidebar-button-bg: rgba(15, 23, 42, 0.72);
        --siq-sidebar-button-text: rgba(226, 232, 240, 0.88);
        --siq-sidebar-star: rgba(148, 163, 184, 0.70);
      }

      html[data-theme="light"],
      body[data-theme="light"],
      html.theme-light,
      body.theme-light {
        --siq-sidebar-bg: #ffffff;
        --siq-sidebar-top-bg: #ffffff;
        --siq-sidebar-border: rgba(15, 23, 42, 0.10);
        --siq-sidebar-label: #64748b;
        --siq-sidebar-label-hover: #0f172a;
        --siq-sidebar-item: #334155;
        --siq-sidebar-item-hover: #0f172a;
        --siq-sidebar-item-hover-bg: #eef4ff;
        --siq-sidebar-item-active-bg: #dbeafe;
        --siq-sidebar-item-active: #0f172a;
        --siq-sidebar-card-bg: #ffffff;
        --siq-sidebar-card-text: #334155;
        --siq-sidebar-card-title: #0f172a;
        --siq-sidebar-foot: #64748b;
        --siq-sidebar-legal: #64748b;
        --siq-sidebar-button-bg: #eef4ff;
        --siq-sidebar-button-text: #334155;
        --siq-sidebar-star: #94a3b8;
      }

      .siq-sidebar-desktop,
      .siq-sidebar-mobile {
        background: var(--siq-sidebar-bg) !important;
        border-right-color: var(--siq-sidebar-border) !important;
        color: var(--siq-sidebar-item) !important;
      }

      .siq-sidebar-top {
        background: var(--siq-sidebar-top-bg) !important;
        border-bottom-color: var(--siq-sidebar-border) !important;
      }

      .siq-sidebar-collapse,
      .siq-mobile-menu {
        background: var(--siq-sidebar-button-bg) !important;
        color: var(--siq-sidebar-button-text) !important;
        border-color: var(--siq-sidebar-border) !important;
      }

      .siq-sidebar-group-label {
        color: var(--siq-sidebar-label) !important;
        opacity: 1 !important;
      }

      .siq-sidebar-group-label:hover {
        color: var(--siq-sidebar-label-hover) !important;
      }

      .siq-sidebar-item {
        color: var(--siq-sidebar-item) !important;
        opacity: 1 !important;
        filter: none !important;
      }

      .siq-sidebar-item:hover {
        background: var(--siq-sidebar-item-hover-bg) !important;
        color: var(--siq-sidebar-item-hover) !important;
      }

      .siq-sidebar-item.active {
        background: var(--siq-sidebar-item-active-bg) !important;
        color: var(--siq-sidebar-item-active) !important;
        border-color: rgba(37, 99, 235, 0.22) !important;
      }

      .siq-sidebar-item-icon {
        color: currentColor !important;
        stroke: currentColor !important;
        opacity: 1 !important;
      }

      .siq-sidebar-item.is-locked {
        opacity: 0.72 !important;
      }

      html[data-theme="light"] .siq-sidebar-item.is-locked,
      html.theme-light .siq-sidebar-item.is-locked {
        opacity: 1 !important;
      }

      .siq-sidebar-onboarding,
      .siq-sidebar-beta-strip,
      .siq-sidebar-institutional-strip {
        background: var(--siq-sidebar-card-bg) !important;
        color: var(--siq-sidebar-card-text) !important;
        border-color: rgba(37, 99, 235, 0.20) !important;
      }

      .siq-sidebar-onboarding-title {
        color: var(--siq-sidebar-card-title) !important;
      }

      .siq-sidebar-onboarding-copy {
        color: var(--siq-sidebar-card-text) !important;
      }

      .siq-sidebar-foot {
        background: var(--siq-sidebar-top-bg) !important;
        color: var(--siq-sidebar-foot) !important;
        border-top-color: var(--siq-sidebar-border) !important;
      }

      .siq-sidebar-legal a {
        color: var(--siq-sidebar-legal) !important;
      }

      /* Code-only Signaliq Intelligence logo: no image/icon dependency */
      .siq-code-logo {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        min-width: 0;
        height: 54px;
        padding: 0 2px;
        color: var(--siq-sidebar-item-active, #ffffff);
      }

      .siq-code-logo--compact {
        justify-content: center;
        width: 44px;
        height: 44px;
      }

      .siq-code-logo-word {
        display: inline-flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
        line-height: 1;
        white-space: nowrap;
      }

      .siq-code-logo-brand {
        font-family: var(--font-ui, Inter, ui-sans-serif, system-ui);
        font-size: 27px;
        line-height: 1;
        font-weight: 850;
        letter-spacing: -0.055em;
        color: var(--siq-sidebar-item-active, #ffffff);
      }

      .siq-code-logo-sub {
        margin-top: 5px;
        font-family: var(--font-mono, "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace);
        font-size: 9px;
        line-height: 1;
        font-weight: 800;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--siq-sidebar-label, rgba(148, 163, 184, 0.74));
      }

      html[data-theme="light"] .siq-code-logo-brand,
      html.theme-light .siq-code-logo-brand {
        color: #0f172a;
      }

      html[data-theme="light"] .siq-code-logo-sub,
      html.theme-light .siq-code-logo-sub {
        color: #64748b;
      }

      .siq-code-logo--compact .siq-code-logo-brand {
        font-size: 18px;
        letter-spacing: -0.04em;
        color: #2563eb;
      }

      .siq-sidebar-favorite-toggle {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 8px;
        color: var(--siq-sidebar-star);
        opacity: 0;
        transition: opacity 140ms ease, color 140ms ease, background 140ms ease;
      }

      .siq-sidebar-item:hover .siq-sidebar-favorite-toggle,
      .siq-sidebar-favorite-toggle.is-favorite,
      .siq-sidebar-favorite-toggle:focus-visible {
        opacity: 1;
      }

      .siq-sidebar-favorite-toggle:hover {
        background: rgba(245, 158, 11, 0.12);
        color: #f59e0b;
      }

      .siq-sidebar-favorite-toggle.is-favorite {
        color: #f59e0b;
      }
    `}</style>
  );
}

function SignalIQLogo({ compact = false }) {
  return (
    <div
      className={compact ? "siq-code-logo siq-code-logo--compact" : "siq-code-logo"}
      aria-label="Signaliq Intelligence"
    >
      <span className="siq-code-logo-word">
        <span className="siq-code-logo-brand">Signaliq</span>
        {!compact && <span className="siq-code-logo-sub">Intelligence</span>}
      </span>
    </div>
  );
}

function SidebarBadge({ children, tone = "blue" }) {
  const styles = {
    blue: {
      background: "rgba(37,99,235,0.14)",
      color: "var(--blue-lt, #93c5fd)",
      border: "1px solid rgba(96,165,250,0.18)",
    },
    amber: {
      background: "rgba(245,158,11,0.12)",
      color: "#fbbf24",
      border: "1px solid rgba(245,158,11,0.18)",
    },
    green: {
      background: "rgba(16,185,129,0.12)",
      color: "#34d399",
      border: "1px solid rgba(16,185,129,0.18)",
    },
  };

  return (
    <span className="siq-sidebar-badge" style={styles[tone] || styles.blue}>
      {children}
    </span>
  );
}

export default function Sidebar() {
  const { hasFeature } = useSubscription();
  const {
    user,
    canAccessAdmin,
    canAccessGovernance,
    canAccessModelLab,
    canAccessProductModules,
  } = useAuth();
  const location = useLocation();

  const role = getUserRole(user);
  const tier = getUserTier(user);

  const isLoggedIn = Boolean(user?.email || user?.id);
  const isAdmin = hasAnyRole(role, ADMIN_ROLES);
  const isTester = isTesterRole(role);
  const hasBetaAccess = isBetaAccessRole(role);
  const hasInstitutionalAccess = Boolean(isAdmin || isTester || isInstitutionalTier(tier));
  const hasPaidProductTier = Boolean(isAdmin || isTester || isProductTier(tier));

  const hasAdminAccess = Boolean(canAccessAdmin?.() || hasAnyRole(role, ADMIN_ROLES));
  const hasGovernanceAccess = Boolean(canAccessGovernance?.() || hasAnyRole(role, GOVERNANCE_ROLES));
  const hasModelLabAccess = Boolean(canAccessModelLab?.() || hasAnyRole(role, ADMIN_ROLES));
  const hasProductAccess = Boolean(canAccessProductModules?.() || hasBetaAccess || hasAdminAccess || hasPaidProductTier);
  const needsOnboarding = shouldShowOnboarding(user);
  const desktopNavRef = useRef(null);
  const mobileNavRef = useRef(null);
  const restoreScrollTimerRef = useRef(null);

  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    try {
      return localStorage.getItem("siq_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favoriteRoutes, setFavoriteRoutes] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SIDEBAR_FAVORITES_STORAGE_KEY) || "[]");
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  });

  const persistSidebarScroll = useCallback((top) => {
    try {
      sessionStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, String(Math.max(0, Math.round(top || 0))));
    } catch {
      /* ignore */
    }
  }, []);

  const restoreSidebarScroll = useCallback(() => {
    let saved = 0;
    try {
      saved = Number(sessionStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY) || 0);
    } catch {
      saved = 0;
    }

    const apply = () => {
      [desktopNavRef.current, mobileNavRef.current].forEach((node) => {
        if (node && Number.isFinite(saved)) {
          node.scrollTop = saved;
        }
      });
    };

    window.requestAnimationFrame(apply);
    window.setTimeout(apply, 60);
    window.setTimeout(apply, 180);
  }, []);

  const handleNavScroll = useCallback(
    (event) => {
      persistSidebarScroll(event.currentTarget.scrollTop);
    },
    [persistSidebarScroll]
  );

  const toggleGroup = (group) => {
    const currentTop = desktopNavRef.current?.scrollTop ?? mobileNavRef.current?.scrollTop ?? 0;
    persistSidebarScroll(currentTop);
    setCollapsedGroups((current) => ({ ...current, [group]: !current[group] }));
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    restoreSidebarScroll();

    return () => {
      if (restoreScrollTimerRef.current) {
        clearTimeout(restoreScrollTimerRef.current);
      }
    };
  }, [location.pathname, collapsedGroups, desktopCollapsed, restoreSidebarScroll]);

  useEffect(() => {
    const handler = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteRoutes));
    } catch {
      /* ignore */
    }
  }, [favoriteRoutes]);

  const toggleFavoriteRoute = useCallback((route) => {
    if (!route) return;
    setFavoriteRoutes((current) => {
      const exists = current.includes(route);
      if (exists) return current.filter((item) => item !== route);
      return [route, ...current].slice(0, 12);
    });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("siq_sidebar_collapsed", String(desktopCollapsed));
    } catch {
      /* ignore */
    }

    // Critical: lets the global AppShell grid switch columns cleanly.
    document.documentElement.classList.toggle("siq-sidebar-collapsed", desktopCollapsed);
    document.documentElement.style.setProperty(
      "--sidebar-expanded-w",
      `${EXPANDED_WIDTH}px`
    );
    document.documentElement.style.setProperty(
      "--sidebar-collapsed-w",
      `${COLLAPSED_WIDTH}px`
    );

    return () => {
      document.documentElement.classList.remove("siq-sidebar-collapsed");
    };
  }, [desktopCollapsed]);

  const NAV = useMemo(
    () => [
      {
        group: "Start",
        defaultOpen: true,
        items: [
          {
            to: APP_ROUTES.onboarding,
            label: "Launch Onboarding",
            icon: Sparkles,
            hidden: !needsOnboarding,
            badge: "Start",
            badgeTone: "green",
          },
          {
            to: APP_ROUTES.companies,
            label: "Company Registry",
            icon: Building2,
            feature: "portfolio_overview",
          },
          {
            to: APP_ROUTES.ingestion,
            label: "Data Ingestion",
            icon: Upload,
            feature: "ingestion",
            badge: "EDGAR",
            badgeTone: "green",
          },
          {
            to: APP_ROUTES.ingestionJobs,
            label: "Ingestion Jobs",
            icon: Activity,
            feature: "ingestion",
          },
          {
            to: APP_ROUTES.financialStatementVisuals,
            label: "Financial Visuals",
            icon: BarChart3,
            feature: "ingestion",
            badge: "Audit",
            badgeTone: "blue",
          },
          {
            to: APP_ROUTES.dashboard,
            label: "Command Center",
            icon: LayoutDashboard,
            feature: "portfolio_overview",
          },
          {
            to: APP_ROUTES.portfolio,
            label: "Portfolio Overview",
            icon: BriefcaseBusiness,
            feature: "portfolio_overview",
          },
        ],
      },
      {
        group: "AI & Intelligence",
        defaultOpen: true,
        items: [
          { to: APP_ROUTES.aiAnalyst, label: "AI Analyst", icon: Bot, feature: "ai_insights" },
          { to: APP_ROUTES.macro, label: "Macro Intelligence", icon: Globe, feature: "macro" },
          { to: APP_ROUTES.dailyBriefing, label: "Daily Briefing", icon: Newspaper, feature: "macro" },
          { to: APP_ROUTES.riskMonitor, label: "Global Risk Monitor", icon: ShieldAlert, feature: "macro" },
        ],
      },
      {
        group: "Core Analysis",
        defaultOpen: true,
        items: [
          { to: APP_ROUTES.bsp, label: "Business Strength", icon: BarChart3, feature: "strength_profile" },
          { to: APP_ROUTES.fhi, label: "Financial Health", icon: PieChart, feature: "fhi" },
          { to: APP_ROUTES.benchmarks, label: "Benchmarks", icon: Scale, feature: "benchmarks" },
          { to: APP_ROUTES.unitEconomics, label: "Unit Economics", icon: Calculator, feature: "unit_economics" },
          { to: APP_ROUTES.variance, label: "Variance", icon: TrendingUp, feature: "variance" },
          { to: APP_ROUTES.decomposition, label: "Decomposition", icon: Layers, feature: "reports" },
        ],
      },
      {
        group: "Risk & Compliance",
        defaultOpen: true,
        items: [
          { to: APP_ROUTES.bspSurvival, label: "Survival Probability", icon: HeartPulse, feature: "bsp" },
          { to: APP_ROUTES.covenants, label: "Covenants", icon: AlertTriangle, feature: "covenants" },
          { to: APP_ROUTES.earlyWarnings, label: "Early Warnings", icon: Zap, feature: "early_warnings" },
          { to: APP_ROUTES.esg, label: "ESG Analytics", icon: Leaf, feature: "esg" },
        ],
      },
      {
        group: "Planning",
        defaultOpen: true,
        items: [
          { to: APP_ROUTES.scenarios, label: "Scenario Analysis", icon: Target, feature: "scenarios" },
          { to: APP_ROUTES.sensitivity, label: "Sensitivity", icon: LineChart, feature: "sensitivity" },
          { to: APP_ROUTES.goalSeek, label: "Goal Seek", icon: Telescope, feature: "goal_seek" },
          { to: APP_ROUTES.forecasting, label: "Forecasting", icon: Gauge, feature: "forecasting" },
          { to: APP_ROUTES.investmentAppraisal, label: "Investment Appraisal", icon: Briefcase, feature: "investment_appraisal" },
          { to: APP_ROUTES.costBenefit, label: "Cost-Benefit", icon: Calculator, feature: "cost_benefit" },
        ],
      },
      {
        group: "Reports",
        defaultOpen: true,
        items: [
          { to: APP_ROUTES.reports, label: "Reports", icon: FileText, feature: "reports" },
        ],
      },
      {
        group: "Workspace",
        defaultOpen: false,
        items: [
          { to: APP_ROUTES.team, label: "Team", icon: Users, roles: ADMIN_ROLES, adminOnly: true },
          { to: APP_ROUTES.billing, label: "Billing", icon: CreditCard, roles: BILLING_ROLES, billingOnly: true },
          { to: APP_ROUTES.settings, label: "Settings", icon: Settings, requiresLogin: true },
        ],
      },
      {
        group: "Platform Modules",
        defaultOpen: false,
        hidden: !hasAdminAccess,
        items: [
          { to: APP_ROUTES.admin, label: "Admin Control", icon: Lock, roles: ADMIN_ROLES, adminOnly: true },
          { to: APP_ROUTES.modelLab, label: "Model Lab", icon: FlaskConical, roles: ADMIN_ROLES, modelLabOnly: true },
          { to: APP_ROUTES.modelGovernance, label: "Model Governance", icon: ShieldCheck, roles: ADMIN_ROLES, governanceOnly: true },
          { to: APP_ROUTES.aiWorkforce, label: "AI Workforce", icon: Cpu, roles: ADMIN_ROLES, adminOnly: true, badge: "ADMIN", badgeTone: "amber" },
        ],
      },
    ],
    [hasAdminAccess, needsOnboarding]
  );

  useEffect(() => {
    setCollapsedGroups((current) => {
      const next = { ...current };
      NAV.forEach((group) => {
        if (next[group.group] === undefined) {
          next[group.group] = group.defaultOpen === false;
        }
      });
      return next;
    });
  }, [NAV]);

  const navGroups = NAV.map((group) => {
    const visibleItems = group.items.filter((item) => {
      if (item.hidden) return false;

      // Testers get full product-module access, but never see admin/governance/model-lab/billing controls.
      // No anonymous/public access to internal controls.
      if (item.requiresLogin && !isLoggedIn) return false;

      // Testers get product-module access, but never internal admin/governance/model/billing/team controls.
      if (isTester && isAdminOnlyItem(item)) return false;

      if (item.adminOnly && !hasAdminAccess) return false;
      if (item.governanceOnly && !hasGovernanceAccess) return false;
      if (item.modelLabOnly && !hasModelLabAccess) return false;
      if (item.billingOnly && !hasAnyRole(role, BILLING_ROLES)) return false;
      if (item.institutionalOnly && !hasInstitutionalAccess) return false;

      if (item.roles && !hasAnyRole(role, item.roles)) return false;

      return true;
    });

    return {
      ...group,
      items: visibleItems,
      hidden: group.hidden || visibleItems.length === 0,
    };
  }).filter((group) => !group.hidden);


  const favoriteItems = useMemo(() => {
    if (!favoriteRoutes.length) return [];

    const visibleByRoute = new Map();
    navGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (item?.to && !visibleByRoute.has(item.to)) {
          visibleByRoute.set(item.to, { ...item, favoriteGroup: true });
        }
      });
    });

    return favoriteRoutes.map((route) => visibleByRoute.get(route)).filter(Boolean);
  }, [favoriteRoutes, navGroups]);

  function NavContent({ compact = false, mobile = false }) {
    const groupsToRender = !compact && favoriteItems.length
      ? [{ group: "Favorites", items: favoriteItems, favoriteGroup: true }, ...navGroups]
      : navGroups;

    return (
      <div className="siq-sidebar-shell">
        <div className="siq-sidebar-top">
          <Link
            to={APP_ROUTES.dashboard}
            className="siq-sidebar-logo"
            aria-label="Go to Signaliq Intelligence dashboard"
            title="Signaliq Intelligence"
          >
            <SignalIQLogo compact={compact} />
          </Link>

          {!mobile && (
            <button
              type="button"
              className="siq-sidebar-collapse"
              onClick={() => setDesktopCollapsed((value) => !value)}
              aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {desktopCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          )}
        </div>

        {hasBetaAccess && !compact && (
          <div className="siq-sidebar-beta-strip">
            <ShieldCheck size={12} />
            Beta access: product modules unlocked
          </div>
        )}

        {!hasBetaAccess && hasInstitutionalAccess && !compact && (
          <div className="siq-sidebar-institutional-strip">
            <ShieldCheck size={12} />
            Institutional workspace
          </div>
        )}

        {needsOnboarding && !compact && (
          <Link to={APP_ROUTES.onboarding} className="siq-sidebar-onboarding">
            <div className="siq-sidebar-onboarding-title">
              <Sparkles size={13} style={{ color: "#7dd3fc" }} />
              Launch setup
            </div>
            <div className="siq-sidebar-onboarding-copy">
              Upload financial data or connect SEC EDGAR to reach first value.
            </div>
          </Link>
        )}

        <div
          ref={mobile ? mobileNavRef : desktopNavRef}
          className="siq-sidebar-nav"
          onScroll={handleNavScroll}
        >
          {groupsToRender.map(({ group, items, favoriteGroup }) => {
            const isCollapsed = collapsedGroups[group];

            return (
              <div key={group}>
                {!compact && (
                  <div
                    className="siq-sidebar-group-label"
                    onClick={() => toggleGroup(group)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleGroup(group);
                      }
                    }}
                  >
                    <span>{group}</span>
                    {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
                  </div>
                )}

                {(compact || !isCollapsed) &&
                  items.map((item) => {
                    const { to, label, icon: Icon, feature, badge, badgeTone } = item;
                    const locked = Boolean(
                      feature &&
                        !hasProductAccess &&
                        !hasFeature(feature) &&
                        !canBypassModuleLock(role, item)
                    );
                    const effectiveBadge = badge || "";
                    const effectiveBadgeTone = badgeTone || "blue";

                    return (
                      <NavLink
                        key={to}
                        to={locked ? APP_ROUTES.billing : to}
                        title={locked ? `Upgrade required for ${titleCase(feature)}` : label}
                        className={({ isActive }) =>
                          `siq-sidebar-item${isActive ? " active" : ""}${locked ? " is-locked" : ""}`
                        }
                        onClick={() => {
                          const top = desktopNavRef.current?.scrollTop ?? mobileNavRef.current?.scrollTop ?? 0;
                          persistSidebarScroll(top);
                        }}
                      >
                        <Icon
                          size={compact ? 18 : 16}
                          className="siq-sidebar-item-icon"
                        />

                        {!compact && (
                          <span className="siq-sidebar-item-label">{label}</span>
                        )}

                        {!compact && !locked && (
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={favoriteRoutes.includes(to) ? `Remove ${label} from favorites` : `Add ${label} to favorites`}
                            title={favoriteRoutes.includes(to) ? "Remove from favorites" : "Add to favorites"}
                            className={`siq-sidebar-favorite-toggle${favoriteRoutes.includes(to) ? " is-favorite" : ""}`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleFavoriteRoute(to);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                event.stopPropagation();
                                toggleFavoriteRoute(to);
                              }
                            }}
                          >
                            <Star size={13} fill={favoriteRoutes.includes(to) ? "currentColor" : "none"} />
                          </span>
                        )}

                        {effectiveBadge && !locked && !compact && (
                          <SidebarBadge tone={effectiveBadgeTone}>{effectiveBadge}</SidebarBadge>
                        )}

                        {locked && !compact && !hasBetaAccess && <SidebarBadge>PRO</SidebarBadge>}
                      </NavLink>
                    );
                  })}
              </div>
            );
          })}
        </div>

        {!compact && (
          <div className="siq-sidebar-foot">
            <div className="siq-sidebar-foot-row">
              <Landmark size={11} />
              <span>Financial intelligence platform</span>
            </div>
            <div className="siq-sidebar-legal">
              {[
                ["/privacy", "Privacy"],
                ["/terms", "Terms"],
                ["/compliance", "Compliance"],
                ["/security", "Security"],
              ].map(([to, label]) => (
                <Link key={to} to={to}>
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const width = desktopCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <>
      <SidebarStyles />

      <nav
        className={`siq-sidebar-desktop${desktopCollapsed ? " is-collapsed" : ""}`}
        aria-label="Primary navigation"
        style={{
          flex: `0 0 ${width}px`,
          width,
          minWidth: width,
          maxWidth: width,
        }}
      >
        <NavContent compact={desktopCollapsed} />
      </nav>

      <button
        className="siq-mobile-menu"
        onClick={() => setMobileOpen((value) => !value)}
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
        aria-expanded={mobileOpen}
        type="button"
      >
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {mobileOpen && (
        <div
          className="siq-sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav
        className={`siq-sidebar-mobile${mobileOpen ? " is-open" : ""}`}
        aria-label="Mobile navigation"
      >
        <NavContent mobile />
      </nav>
    </>
  );
}

